"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { useReportReload } from "@/hooks/use-report-reload";

interface ReportWidgetContext {
  isSplitView: boolean;
  setIsSplitView: (value: boolean) => void;
  splitPosition: number;
  setSplitPosition: (value: number) => void;
  // Добавляем функциональность для обновления данных отчета
  reloadReport: (recordId?: string) => Promise<void>;
  isReloading: boolean;
}

const ReportWidgetCtx = createContext<ReportWidgetContext | undefined>(
  undefined
);

export const ReportWidgetProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isSplitView, setIsSplitView] = useState<boolean>(false);
  const [splitPosition, setSplitPosition] = useState<number>(50);
  const [isReloading, setIsReloading] = useState<boolean>(false);

  // Используем хук для обновления данных отчетов
  const { reloadReport: reloadReportHook } = useReportReload();

  // Функция для обновления данных отчета
  const reloadReport = useCallback(
    async (recordId?: string) => {
      try {
        setIsReloading(true);

        // Вызываем реальную логику обновления через хук
        await reloadReportHook(recordId);
      } catch (error) {
        console.error(
          "ReportWidgetContext: Failed to reload report data:",
          error
        );
        throw error; // Пробрасываем ошибку для обработки в компонентах
      } finally {
        setIsReloading(false);
      }
    },
    [reloadReportHook]
  );

  const values: ReportWidgetContext = {
    isSplitView,
    setIsSplitView,
    splitPosition,
    setSplitPosition,
    reloadReport,
    isReloading,
  };

  return (
    <ReportWidgetCtx.Provider value={values}>
      {children}
    </ReportWidgetCtx.Provider>
  );
};

export const useOptionalReportWidgetContext = () => {
  return useContext(ReportWidgetCtx);
};

export const useReportWidgetContext = (): ReportWidgetContext => {
  const context = useContext(ReportWidgetCtx);
  if (context === undefined) {
    throw new Error(
      "useReportWidgetContext must be used within a ReportWidgetProvider"
    );
  }
  return context;
};
